import React from 'react';

interface SkeletonListProps {
  /**
   * Number of placeholder rows to render. Defaults to 6 — enough
   * to fill most viewports without overwhelming the user with
   * shimmer.
   */
  rowCount?: number;
  /**
   * Number of table columns. Defaults to 6 which fits most
   * eClinic list pages.
   */
  columnCount?: number;
  /**
   * Optional CSS class for the wrapping div. Use this to control
   * spacing (e.g. "py-4").
   */
  className?: string;
}

/**
 * Shimmer-loading list placeholder. Renders N rows × M columns of
 * subtle animated placeholders while data is fetching. Replaces the
 * bare centered spinner + "Cargando..." text pattern with a layout-
 * preserving skeleton that gives the user a sense of structure.
 *
 * Frontend-design principle: loading states should preserve the
 * page structure (perceived performance), not collapse to a centered
 * spinner (which makes the page feel slower).
 *
 * The CSS animation uses a CSS-only shimmer keyframe (no extra deps).
 */
export const SkeletonList: React.FC<SkeletonListProps> = ({
  rowCount = 6,
  columnCount = 6,
  className = 'py-4',
}) => (
  <>
    <style>{`
      @keyframes eCliniq-skeleton-shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      .eCliniq-skeleton-cell {
        background: linear-gradient(
          90deg,
          #f1f5f9 0%,
          #e2e8f0 50%,
          #f1f5f9 100%
        );
        background-size: 200% 100%;
        animation: eCliniq-skeleton-shimmer 1.5s ease-in-out infinite;
        border-radius: 4px;
        height: 14px;
        margin: 8px 12px;
      }
    `}</style>
    <div className={className} role="status" aria-label="Cargando datos">
      {Array.from({ length: rowCount }).map((_, rowIdx) => (
        <div
          key={rowIdx}
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
            gap: 8,
            padding: '12px 16px',
            borderBottom: '1px solid #f1f5f9',
          }}
        >
          {Array.from({ length: columnCount }).map((_, colIdx) => (
            <div
              key={colIdx}
              className="eCliniq-skeleton-cell"
              style={{ width: colIdx === 0 ? '80%' : colIdx === columnCount - 1 ? '60%' : '90%' }}
            />
          ))}
        </div>
      ))}
    </div>
  </>
);

export default SkeletonList;
